import {Editor, Element, Node, Path, Range, type NodeEntry} from "slate";
import {ReactEditor} from "slate-react";

export const isElement = (node: Node): node is Element => {
    return !Editor.isEditor(node) && Element.isElement(node);
};

export const isElementOfType = <T extends Element>(
    node: Node,
    ...types: T["type"][]
): node is T => {
    if (!isElement(node)) {
        return false;
    }

    for (const t of types) {
        if (node.type === t) {
            return true;
        }
    }

    return false;
};

export const getPreviousSibling = (
    editor: Editor,
    path: Path,
): NodeEntry | null => {
    let previousSiblingPath: Path;

    try {
        previousSiblingPath = Path.previous(path);
    } catch (error) {
        // Unable to calculate `Path.previous`, which means there is no previous sibling.
        return null;
    }

    if (Node.has(editor, previousSiblingPath)) {
        return [Node.get(editor, previousSiblingPath), previousSiblingPath];
    }

    return null;
};

export const getNextSibling = (
    editor: Editor,
    path: Path,
): NodeEntry | null => {
    let nextSiblingPath: Path;

    try {
        nextSiblingPath = Path.next(path);
    } catch (error) {
        // Unable to calculate `Path.next`, which means there is no next sibling.
        return null;
    }

    if (Node.has(editor, nextSiblingPath)) {
        return [Node.get(editor, nextSiblingPath), nextSiblingPath];
    }

    return null;
};

export const isSelectionWithinNode = (editor: Editor, node: Node): boolean => {
    if (!editor.selection) {
        return false;
    }

    const path = ReactEditor.findPath(editor, node);

    if (Range.isCollapsed(editor.selection)) {
        return Path.isAncestor(path, editor.selection.anchor.path);
    }

    return (
        Path.isAncestor(path, editor.selection.anchor.path) &&
        Path.isAncestor(path, editor.selection.focus.path)
    );
};
